import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QuoteHeader } from "@/components/quote-public/QuoteHeader";
import { QuoteLines } from "@/components/quote-public/QuoteLines";
import { AddonsSection } from "@/components/quote-public/AddonsSection";
import { QuoteSummary } from "@/components/quote-public/QuoteSummary";
import { ConfirmDialog } from "@/components/quote-public/ConfirmDialog";
import { FinalState } from "@/components/quote-public/FinalState";
import { ChecklistOfferSection } from "@/components/quote-public/ChecklistOfferSection";
import { PublicI18nContext, DEFAULT_TEXTS, makeT } from "@/components/quote-public/i18n";
import { eur } from "@/components/quote-public/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Iter 159 — Mensajes de error en el idioma del cliente (por código HTTP).
const errMsg = (e, t) => {
  const st = e?.response?.status;
  if (st === 404) return t("err_invalid_link");
  if (st === 410) return t("expired_text");
  if (st === 409) return t("err_already");
  if (st === 400) return t("err_addon_unavailable");
  return t("err_generic");
};

/** Iter 152 — Página pública del presupuesto (/presupuesto/:token). */
export default function QuotePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null); // "accept" | "reject"
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);
  const inFlight = useRef(false);
  const texts = data?.texts || DEFAULT_TEXTS;
  const locale = data?.language?.code || "es-ES";
  const t = useMemo(() => makeT(texts), [texts]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/quote/${token}`);
      setData(r.data);
      setSelected(r.data?.selection?.addon_ids || []);
    } catch (e) {
      setError(errMsg(e, makeT(DEFAULT_TEXTS)));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.documentElement.lang = locale.split("-")[0]; }, [locale]);

  const addonsById = useMemo(() => Object.fromEntries((data?.addons || []).map((a) => [a.id, a])), [data]);
  const extras = selected.map((id) => addonsById[id]).filter(Boolean);
  const extrasTotal = Math.round(extras.reduce((s, a) => s + Number(a.price || 0), 0) * 100) / 100;
  const repairTotal = Number(data?.totals?.total || 0);
  const grandTotal = Math.round((repairTotal + extrasTotal) * 100) / 100;
  const summary = { repairTotal, extras, extrasTotal, grandTotal };

  const persistSelection = (ids) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/public/quote/${token}/selection`, { addon_ids: ids });
      } catch (e) {
        toast.error(errMsg(e, t));
        load();
      }
    }, 350);
  };

  const toggle = (addon) => {
    if (data?.state !== "pending") return;
    setSelected((prev) => {
      const next = prev.includes(addon.id) ? prev.filter((x) => x !== addon.id) : [...prev, addon.id];
      persistSelection(next);
      return next;
    });
  };

  const decide = async (kind) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const url = `${API}/public/quote/${token}/${kind}`;
      const r = kind === "accept" ? await axios.post(url, { addon_ids: selected }) : await axios.post(url);
      setData(r.data);
      setConfirm(null);
      toast.success(kind === "accept" ? t("accepted_toast") : t("rejected_toast"));
    } catch (e) {
      toast.error(errMsg(e, t));
      load();
      setConfirm(null);
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center p-4">
        <div className="ds-card p-8 max-w-md text-center" data-testid="quote-invalid">
          <div className="font-display font-black text-xl">iRepairs</div>
          <h1 className="text-lg font-bold mt-3">{DEFAULT_TEXTS.link_unavailable}</h1>
          <p className="text-sm text-[#525252] mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center p-4">
        <div className="font-mono text-xs uppercase tracking-widest text-[#525252]" data-testid="quote-loading">{DEFAULT_TEXTS.loading}</div>
      </div>
    );
  }

  const pending = data.state === "pending";
  return (
    <PublicI18nContext.Provider value={{ texts, locale }}>
    <div className="min-h-screen bg-[#F7F7F4] py-5 px-3 sm:px-4 pb-40 sm:pb-10" data-testid="quote-public-page" data-lang={locale}>
      <div className="max-w-2xl mx-auto space-y-3">
        <QuoteHeader company={data.company} repair={data.repair} state={data.state} validUntil={data.valid_until} />
        {!pending && <FinalState data={data} />}
        <ChecklistOfferSection token={token} offers={data.checklist_offers} onChanged={load} />
        <QuoteLines items={data.items} totals={data.totals} notes={data.repair.quote_notes} />
        {pending && <AddonsSection addons={data.addons} selectedIds={selected} onToggle={toggle} disabled={busy} />}

        {pending && (
          <div className="hidden sm:block ds-card p-5" data-testid="quote-summary-desktop">
            <QuoteSummary {...summary} testidPrefix="desk" />
            <DecisionButtons onAccept={() => setConfirm("accept")} onReject={() => setConfirm("reject")} busy={busy} idPrefix="desk" t={t} />
          </div>
        )}
        <div className="text-center text-[10px] font-mono uppercase tracking-widest text-[#8A9098] pt-2">
          {data.company?.name} {data.company?.phone ? `· ${data.company.phone}` : ""}
        </div>
      </div>

      {pending && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E5E0] px-4 pt-3 pb-4" style={{ boxShadow: "0 -8px 24px rgba(15,18,16,.08)" }} data-testid="quote-summary-mobile">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{t("total_vat")}</div>
              {extras.length > 0 && <div className="text-[11px] text-[#3F5222]">{t("extras_count", { n: extras.length })} · {eur(extrasTotal)}</div>}
            </div>
            <div className="font-display font-black text-2xl" data-testid="mobile-grand-total">{eur(grandTotal)}</div>
          </div>
          <DecisionButtons onAccept={() => setConfirm("accept")} onReject={() => setConfirm("reject")} busy={busy} compact idPrefix="mobile" t={t} />
        </div>
      )}

      {confirm && (
        <ConfirmDialog kind={confirm} onClose={() => !busy && setConfirm(null)} onConfirm={() => decide(confirm)} busy={busy} summary={summary} />
      )}
    </div>
    </PublicI18nContext.Provider>
  );
}

function DecisionButtons({ onAccept, onReject, busy, compact, idPrefix, t }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${compact ? "mt-3" : "mt-4"}`}>
      <button type="button" onClick={onAccept} disabled={busy}
        className="ds-btn primary justify-center min-h-[48px] text-sm font-bold tracking-wide" data-testid={`${idPrefix}-accept-btn`}>
        {t("accept").toUpperCase()}
      </button>
      <button type="button" onClick={onReject} disabled={busy}
        className="ds-btn danger justify-center min-h-[48px] text-sm font-bold tracking-wide" data-testid={`${idPrefix}-reject-btn`}>
        {t("reject").toUpperCase()}
      </button>
    </div>
  );
}
